/**
 * AgentCards – shared card components for rendering structured agent output.
 *
 * Used in the AssistantChatPage (freeform).
 * Whenever the agent returns an action of a known type, the matching card is rendered
 * in-line in the chat instead of showing raw JSON.
 */

import React, { useEffect, useRef, useState } from 'react';
import type { AgentAction, ImagePaletteColor, PageKey, ThemeColors } from '../types';
import './AgentCards.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Parse raw agent job output into { message, actions }. */
export function parseAgentOutput(output: unknown): { message: string; actions: AgentAction[] } {
  if (!output) return { message: '', actions: [] };

  const parseIfJsonString = (value: unknown): unknown => {
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  let parsed: Record<string, unknown>;

  if (typeof output === 'string') {
    const maybeObj = parseIfJsonString(output);
    if (typeof maybeObj === 'object' && maybeObj !== null) {
      parsed = maybeObj as Record<string, unknown>;
    } else {
      return { message: output, actions: [] };
    }
  } else if (typeof output === 'object' && output !== null) {
    parsed = output as Record<string, unknown>;
  } else {
    return { message: String(output), actions: [] };
  }

  // Some backend paths may store nested JSON as the "message" string.
  // If so, unwrap it so chat shows the real text and cards render.
  const nested = parseIfJsonString(parsed.message);
  if (typeof nested === 'object' && nested !== null) {
    const nestedObj = nested as Record<string, unknown>;
    const nestedMessage = typeof nestedObj.message === 'string' ? nestedObj.message : '';
    const nestedActions = Array.isArray(nestedObj.actions)
      ? (nestedObj.actions as AgentAction[])
      : [];
    return { message: nestedMessage, actions: nestedActions };
  }

  const message = typeof parsed.message === 'string' ? parsed.message : '';
  const actions = Array.isArray(parsed.actions) ? (parsed.actions as AgentAction[]) : [];

  return { message, actions };
}

// ─── Individual card components ───────────────────────────────────────────────

export function StrategyCard({ action }: { action: AgentAction }) {
  // Oracle-authored strategies arrive as open sections; legacy cards use flat fields.
  const sections = Array.isArray(action.sections) ? action.sections : [];
  if (sections.length > 0) {
    return (
      <div className="ob-card ob-card--strategy">
        <div className="ob-card__header">
          <span className="ob-card__icon">🗺️</span>
          <span className="ob-card__title">{action.label || 'Your Strategy'}</span>
        </div>
        <div className="ob-card__body">
          {sections.map((s, i) => (
            <div key={s.key || i} className="ob-card__field">
              <div className="ob-card__field-label">
                {s.title || s.key.replace(/_/g, ' ')}
              </div>
              <div className="ob-card__field-value">{s.body}</div>
            </div>
          ))}
          {action.posting_cadence && (
            <div className="ob-card__field">
              <div className="ob-card__field-label">📅 Posting Cadence</div>
              <div className="ob-card__field-value">{action.posting_cadence}</div>
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="ob-card ob-card--strategy">
      <div className="ob-card__header">
        <span className="ob-card__icon">🗺️</span>
        <span className="ob-card__title">{action.label || 'Your Strategy'}</span>
      </div>
      <div className="ob-card__body">
        {action.north_star && (
          <div className="ob-card__field">
            <div className="ob-card__field-label">⭐ North Star</div>
            <div className="ob-card__field-value">{action.north_star}</div>
          </div>
        )}
        {action.target_audience && (
          <div className="ob-card__field">
            <div className="ob-card__field-label">👥 Target Audience</div>
            <div className="ob-card__field-value">{action.target_audience}</div>
          </div>
        )}
        {action.brand_vibe && (
          <div className="ob-card__field">
            <div className="ob-card__field-label">✨ Brand Vibe</div>
            <div className="ob-card__field-value">{action.brand_vibe}</div>
          </div>
        )}
        {action.image_style && (
          <div className="ob-card__field">
            <div className="ob-card__field-label">🖼️ Image Style</div>
            <div className="ob-card__field-value">{action.image_style}</div>
          </div>
        )}
        {action.image_orientation && (
          <div className="ob-card__field">
            <div className="ob-card__field-label">📐 Image Orientation</div>
            <div className="ob-card__field-value">{action.image_orientation}</div>
          </div>
        )}
        {action.text_on_image && (
          <div className="ob-card__field">
            <div className="ob-card__field-label">📝 Text on Image</div>
            <div className="ob-card__field-value">{action.text_on_image}</div>
          </div>
        )}
        {action.video_style && (
          <div className="ob-card__field">
            <div className="ob-card__field-label">🎬 Video Style</div>
            <div className="ob-card__field-value">{action.video_style}</div>
          </div>
        )}
        {action.caption_style && (
          <div className="ob-card__field">
            <div className="ob-card__field-label">✍️ Caption Style</div>
            <div className="ob-card__field-value">{action.caption_style}</div>
          </div>
        )}
        {action.hashtag_policy && (
          <div className="ob-card__field">
            <div className="ob-card__field-label"># Hashtag Policy</div>
            <div className="ob-card__field-value">{action.hashtag_policy}</div>
          </div>
        )}
        {action.posting_cadence && (
          <div className="ob-card__field">
            <div className="ob-card__field-label">📅 Posting Cadence</div>
            <div className="ob-card__field-value">{action.posting_cadence}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export function PillarsCard({ action }: { action: AgentAction }) {
  const pillars = action.pillars || [];
  return (
    <div className="ob-card ob-card--pillars">
      <div className="ob-card__header">
        <span className="ob-card__icon">🏛️</span>
        <span className="ob-card__title">{action.label || 'Content Pillars'}</span>
        <span className="ob-card__badge">{pillars.length} pillars</span>
      </div>
      <div className="ob-pillars-grid">
        {pillars.map((p, i) => (
          <div key={p.id || i} className="ob-pillar-chip">
            <div className="ob-pillar-chip__name">{p.name}</div>
            {p.description && <div className="ob-pillar-chip__desc">{p.description}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function PlanCard({ action }: { action: AgentAction }) {
  const hasOverrides =
    action.override_brand_vibe ||
    action.override_image_style ||
    action.override_text_on_image ||
    action.override_video_style ||
    action.override_caption_style ||
    action.override_hashtag_policy;
  return (
    <div className="ob-card ob-card--plan">
      <div className="ob-card__header">
        <span className="ob-card__icon">📅</span>
        <span className="ob-card__title">{action.label || 'Content Plan'}</span>
        <span className="ob-card__badge ob-card__badge--optional">optional</span>
      </div>
      <div className="ob-card__body">
        {action.name && (
          <div className="ob-card__field">
            <div className="ob-card__field-label">Plan Name</div>
            <div className="ob-card__field-value ob-card__field-value--large">{action.name}</div>
          </div>
        )}
        {action.description && (
          <div className="ob-card__field">
            <div className="ob-card__field-label">Description</div>
            <div className="ob-card__field-value">{action.description}</div>
          </div>
        )}
        {(action.start_date || action.end_date) && (
          <div className="ob-card__dates">
            {action.start_date && <span className="ob-card__date-tag">📅 {action.start_date}</span>}
            {action.end_date && <span className="ob-card__date-tag">→ {action.end_date}</span>}
          </div>
        )}
        {hasOverrides && (
          <div
            className="ob-card__field"
            style={{ marginTop: 8, borderTop: '1px solid var(--border-color)', paddingTop: 8 }}
          >
            <div className="ob-card__field-label">🎨 Campaign Overrides</div>
            {action.override_brand_vibe && (
              <div className="ob-card__field-value" style={{ fontSize: 12, marginTop: 4 }}>
                <strong>Brand Vibe:</strong> {action.override_brand_vibe}
              </div>
            )}
            {action.override_image_style && (
              <div className="ob-card__field-value" style={{ fontSize: 12, marginTop: 4 }}>
                <strong>Image Style:</strong> {action.override_image_style}
              </div>
            )}
            {action.override_text_on_image && (
              <div className="ob-card__field-value" style={{ fontSize: 12, marginTop: 4 }}>
                <strong>Text on Image:</strong> {action.override_text_on_image}
              </div>
            )}
            {action.override_video_style && (
              <div className="ob-card__field-value" style={{ fontSize: 12, marginTop: 4 }}>
                <strong>Video Style:</strong> {action.override_video_style}
              </div>
            )}
            {action.override_caption_style && (
              <div className="ob-card__field-value" style={{ fontSize: 12, marginTop: 4 }}>
                <strong>Caption Style:</strong> {action.override_caption_style}
              </div>
            )}
            {action.override_hashtag_policy && (
              <div className="ob-card__field-value" style={{ fontSize: 12, marginTop: 4 }}>
                <strong>Hashtags:</strong> {action.override_hashtag_policy}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function OutlinesCard({ action }: { action: AgentAction }) {
  const outlines = action.outlines || [];
  const formatIcon: Record<string, string> = {
    image: '🖼️',
    video: '🎬',
    carousel: '🗂️',
    reel: '📱',
    story: '📸',
  };
  return (
    <div className="ob-card ob-card--outlines">
      <div className="ob-card__header">
        <span className="ob-card__icon">📝</span>
        <span className="ob-card__title">{action.label || 'Outlines'}</span>
        <span className="ob-card__badge">{outlines.length} outlines</span>
      </div>
      <div className="ob-outlines-list">
        {outlines.map((o, i) => (
          <div key={o.id || i} className="ob-outline-row">
            <span className="ob-outline-row__num">{i + 1}</span>
            <div className="ob-outline-row__body">
              <div className="ob-outline-row__title">{o.title}</div>
              {o.summary && <div className="ob-outline-row__summary">{o.summary}</div>}
            </div>
            <div className="ob-outline-row__meta">
              <span className="ob-outline-row__format">
                {formatIcon[o.content_format] || '📄'} {o.content_format}
              </span>
              {o.pillar_name && <span className="ob-outline-row__pillar">{o.pillar_name}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ContentCard({ action }: { action: AgentAction }) {
  return (
    <div className="ob-card ob-card--content">
      <div className="ob-card__header">
        <span className="ob-card__icon">✨</span>
        <span className="ob-card__title">{action.label || 'Post Ready!'}</span>
      </div>
      <div className="ob-card__body">
        {action.image_url && (
          <div className="ob-content-image">
            <img src={action.image_url} alt={action.outline_title || 'Your first post'} />
          </div>
        )}
        {action.outline_title && (
          <div className="ob-content-post-title">{action.outline_title}</div>
        )}
        {action.caption && (
          <div className="ob-card__field">
            <div className="ob-card__field-label">📝 Caption</div>
            <div className="ob-content-caption">{action.caption}</div>
          </div>
        )}
        {action.hashtags && <div className="ob-content-hashtags">{action.hashtags}</div>}
        {action.image_prompts && action.image_prompts.length > 0 && (
          <div className="ob-card__field">
            <div className="ob-card__field-label">🎨 Visual Direction</div>
            {action.image_prompts.map((p, i) => (
              <div key={i} className="ob-image-prompt">
                {p}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function ResearchCard({ action }: { action: AgentAction }) {
  const findings = action.findings || [];
  const sources = action.sources || [];
  return (
    <div className="ob-card ob-card--research">
      <div className="ob-card__header">
        <span className="ob-card__icon">🔭</span>
        <span className="ob-card__title">{action.label || 'Niche scan'}</span>
      </div>
      <div className="ob-card__body">
        {action.headline && <div className="ob-research-headline">{action.headline}</div>}
        {findings.map((f, i) => (
          <div key={i} className="ob-card__field">
            <div className="ob-card__field-label">{f.title}</div>
            <div className="ob-card__field-value">{f.detail}</div>
          </div>
        ))}
        {action.angle && (
          <div className="ob-research-angle">
            <div className="ob-card__field-label">🎯 Your angle</div>
            <div className="ob-card__field-value">{action.angle}</div>
          </div>
        )}
        {sources.length > 0 && (
          <div className="ob-research-sources">
            {sources.map((s, i) => (
              <a key={i} href={s.url} target="_blank" rel="noreferrer">
                {s.title || new URL(s.url).hostname}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function IdentityCard({ action }: { action: AgentAction }) {
  const handles = action.handle_ideas || [];
  return (
    <div className="ob-card ob-card--identity">
      <div className="ob-card__header">
        <span className="ob-card__icon">🪪</span>
        <span className="ob-card__title">{action.label || 'Your identity'}</span>
      </div>
      <div className="ob-card__body">
        {action.display_name && (
          <div className="ob-identity-name">{action.display_name}</div>
        )}
        {action.bio && <div className="ob-identity-bio">{action.bio}</div>}
        {handles.length > 0 && (
          <div className="ob-card__field">
            <div className="ob-card__field-label">Handle ideas</div>
            <div className="ob-identity-handles">
              {handles.map((h, i) => (
                <span key={i} className="ob-identity-handle">
                  {h.startsWith('@') ? h : `@${h}`}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ImageAssetCard({ action }: { action: AgentAction }) {
  if (!action.image_url) return null;
  const purposeLabel =
    action.purpose === 'profile_image' ? 'Profile picture' : 'Brand icon';
  return (
    <div className="ob-card ob-card--image-asset">
      <div className="ob-card__header">
        <span className="ob-card__icon">🎨</span>
        <span className="ob-card__title">{action.label || purposeLabel}</span>
      </div>
      <div className="ob-image-asset">
        <img
          src={action.image_url}
          alt={action.label || purposeLabel}
          className={action.purpose === 'profile_image' ? 'ob-image-asset--round' : ''}
        />
      </div>
    </div>
  );
}

export function SocialAccountCTA({ onNavigate }: { onNavigate?: (_page: PageKey) => void }) {
  return (
    <div className="ob-card ob-card--social">
      <div className="ob-card__header">
        <span className="ob-card__icon">📸</span>
        <span className="ob-card__title">Connect Your Instagram</span>
      </div>
      <div className="ob-social-cta">
        <p className="ob-social-cta__text">
          Your content is ready! Now connect your Instagram account so Conistra can publish for you.
        </p>
        <button className="ob-social-cta__btn" onClick={() => onNavigate?.('settings')}>
          Go to Settings → Connect Instagram
        </button>
      </div>
    </div>
  );
}

export function SettingsCard({ action }: { action: AgentAction }) {
  const settings = [
    {
      key: 'auto_approve',
      label: 'Auto-Approve Content',
      desc: 'AI creates and approves content automatically — no manual review',
      value: action.auto_approve,
      icon: '✅',
    },
    {
      key: 'auto_publish',
      label: 'Auto-Publish',
      desc: 'Conistra posts directly to Instagram without asking you first',
      value: action.auto_publish,
      icon: '🚀',
    },
    {
      key: 'scheduler_enabled',
      label: 'Smart Scheduler',
      desc: 'Automatically picks the best times to publish your posts',
      value: action.scheduler_enabled,
      icon: '📅',
    },
  ];
  return (
    <div className="ob-card ob-card--settings">
      <div className="ob-card__header">
        <span className="ob-card__icon">⚙️</span>
        <span className="ob-card__title">{action.label || 'Workspace Settings'}</span>
      </div>
      <div className="ob-settings-list">
        {settings.map((s) => (
          <div key={s.key} className="ob-setting-row">
            <span className="ob-setting-row__icon">{s.icon}</span>
            <div className="ob-setting-row__body">
              <div className="ob-setting-row__label">{s.label}</div>
              <div className="ob-setting-row__desc">{s.desc}</div>
            </div>
            <div className={`ob-setting-row__status ${s.value ? 'on' : 'off'}`}>
              {s.value ? 'On' : 'Off'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ScheduleCard({
  action,
  onSend,
}: {
  action: AgentAction;
  onSend?: (_text: string) => void;
}) {
  const nextDate = action.next_post_date ? new Date(action.next_post_date) : null;
  const queueEnds = action.queue_ends_at ? new Date(action.queue_ends_at) : null;

  const fmtDate = (d: Date) =>
    d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const fmtTime = (d: Date) =>
    d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="ob-card ob-card--schedule">
      <div className="ob-card__header">
        <span className="ob-card__icon">🗓️</span>
        <span className="ob-card__title">{action.label || 'Publishing Schedule'}</span>
        <span className={`ob-card__badge${action.scheduler_enabled ? '' : ' ob-card__badge--off'}`}>
          {action.scheduler_enabled ? 'Scheduler on' : 'Scheduler off'}
        </span>
      </div>
      <div className="ob-card__body">
        {action.next_post_title && nextDate ? (
          <div className="ob-card__field">
            <div className="ob-card__field-label">▶️ Next post</div>
            <div className="ob-card__field-value ob-card__field-value--large">
              {action.next_post_title}
            </div>
            <div className="ob-schedule-date">
              {fmtDate(nextDate)} at {fmtTime(nextDate)}
            </div>
          </div>
        ) : (
          <div className="ob-card__field">
            <div className="ob-card__field-value ob-schedule-empty">No posts scheduled yet</div>
          </div>
        )}

        <div className="ob-schedule-stats">
          <div className="ob-schedule-stat">
            <span className="ob-schedule-stat__value">{action.queued_count ?? 0}</span>
            <span className="ob-schedule-stat__label">posts queued</span>
          </div>
          {queueEnds && (
            <div className="ob-schedule-stat">
              <span className="ob-schedule-stat__value">
                {queueEnds.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </span>
              <span className="ob-schedule-stat__label">queue runs out</span>
            </div>
          )}
          {typeof action.unscheduled_count === 'number' && action.unscheduled_count > 0 && (
            <div className="ob-schedule-stat ob-schedule-stat--warn">
              <span className="ob-schedule-stat__value">{action.unscheduled_count}</span>
              <span className="ob-schedule-stat__label">unscheduled</span>
            </div>
          )}
        </div>

        {action.next_action && (
          <button
            className="ob-schedule-action-btn"
            onClick={() => action.next_action && onSend?.(action.next_action)}
          >
            {action.next_action}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Theme Card ───────────────────────────────────────────────────────────────

/**
 * What the card is about: the colours the account's *pictures* will be painted
 * in. The nine-stop primary/accent scale is a web-app implementation detail —
 * showing it here answered a question nobody asked. The backend writes this
 * palette into the strategy's `image_style`, so these swatches are a real
 * promise about the feed, not decoration.
 */
function imagePaletteFrom(action: AgentAction, colors: ThemeColors | null): ImagePaletteColor[] {
  const clean = (action.image_palette || []).filter(
    (c): c is ImagePaletteColor => Boolean(c) && typeof c.hex === 'string' && c.hex.startsWith('#')
  );
  if (clean.length) return clean.slice(0, 6);
  // Accounts themed before the image palette existed still get a picture-shaped
  // answer, derived from the family the app is already wearing.
  if (!colors) return [];
  return [
    { hex: colors.primary_900, role: 'depth' },
    { hex: colors.primary_600, role: 'main' },
    { hex: colors.primary_200, role: 'light' },
    { hex: colors.accent_500, role: 'accent' },
  ].filter((c) => Boolean(c.hex));
}

function PaintSwatch({ color }: { color: ImagePaletteColor }) {
  return (
    <div className="ob-paint" title={`${color.name || color.hex}${color.role ? ` — ${color.role}` : ''}`}>
      <span className="ob-paint__chip" style={{ background: color.hex }} />
      <span className="ob-paint__name">{color.name || color.hex}</span>
      <span className="ob-paint__meta">{color.role || color.hex}</span>
    </div>
  );
}

/** Three grid tiles mixed from the palette — what a feed in these colours looks like. */
function FeedPreview({ palette }: { palette: ImagePaletteColor[] }) {
  if (palette.length < 2) return null;
  const tiles = [0, 1, 2].map((i) => {
    const a = palette[i % palette.length].hex;
    const b = palette[(i + 1) % palette.length].hex;
    const c = palette[(i + 2) % palette.length].hex;
    return `linear-gradient(${140 + i * 35}deg, ${a} 0%, ${b} 55%, ${c} 100%)`;
  });
  return (
    <div className="ob-paint-feed" aria-hidden="true">
      {tiles.map((bg, i) => (
        <span key={i} className="ob-paint-feed__tile" style={{ background: bg }} />
      ))}
    </div>
  );
}

export function ThemeCard({
  action,
  onThemeApplied,
}: {
  action: AgentAction;
  onThemeApplied?: (_colors: ThemeColors) => void;
}) {
  const [revealed, setRevealed] = useState(false);
  const hasApplied = useRef(false);

  const requiredFields = [
    'primary_50', 'primary_100', 'primary_200', 'primary_500',
    'primary_600', 'primary_700', 'primary_900', 'accent_500', 'accent_600',
  ] as const;
  const allPresent = requiredFields.every(
    (k) => typeof action[k] === 'string' && (action[k] as string).startsWith('#')
  );

  const colors: ThemeColors | null = allPresent
    ? {
        primary_50: action.primary_50 as string,
        primary_100: action.primary_100 as string,
        primary_200: action.primary_200 as string,
        primary_500: action.primary_500 as string,
        primary_600: action.primary_600 as string,
        primary_700: action.primary_700 as string,
        primary_900: action.primary_900 as string,
        accent_500: action.accent_500 as string,
        accent_600: action.accent_600 as string,
      }
    : null;

  const palette = imagePaletteFrom(action, colors);

  useEffect(() => {
    if (colors && !hasApplied.current) {
      hasApplied.current = true;
      // Small delay for dramatic effect — let the message appear first
      const t = setTimeout(() => {
        setRevealed(true);
        onThemeApplied?.(colors);
      }, 400);
      return () => clearTimeout(t);
    }
    setRevealed(true);
  // Only run on first mount — colors object identity changes each render but values are stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!palette.length && !colors) return null;

  return (
    <div className={`ob-card ob-card--theme ${revealed ? 'ob-card--theme-revealed' : ''}`}>
      <div className="ob-card__header">
        <span className="ob-card__icon">🎨</span>
        <span className="ob-card__title">{action.label || 'Your Colors'}</span>
      </div>
      <div className="ob-card__body">
        <p className="ob-theme-intro">
          These are the colors your posts get painted in. Every image is mixed from this
          set — one picture may lean on a single color, but nothing arrives off-family.
        </p>
        {palette.length > 0 && (
          <>
            <div className="ob-paint-row">
              {palette.map((c, i) => (
                <PaintSwatch key={`${c.hex}-${i}`} color={c} />
              ))}
            </div>
            <FeedPreview palette={palette} />
          </>
        )}
        {colors && (
          <p className="ob-theme-note">
            <span className="ob-theme-note__dots" aria-hidden="true">
              {[colors.primary_600, colors.primary_700, colors.accent_500].map((c, i) => (
                <span key={i} style={{ background: c }} />
              ))}
            </span>
            Your app just took the same family, so the product and the feed match.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Dispatcher ───────────────────────────────────────────────────────────────

/**
 * Renders the appropriate card component for a given agent action.
 * Returns null for unknown action types so unknown actions are silently ignored.
 */
export function renderActionCard(
  action: AgentAction,
  idx: number,
  onNavigate?: (_page: PageKey) => void,
  onSend?: (_text: string) => void,
  onThemeApplied?: (_colors: ThemeColors) => void
): React.ReactElement | null {
  switch (action.type) {
    case 'STRATEGY_CARD':
      return <StrategyCard key={idx} action={action} />;
    case 'PILLARS_CARD':
      return <PillarsCard key={idx} action={action} />;
    case 'PLAN_CARD':
      return <PlanCard key={idx} action={action} />;
    case 'OUTLINES_CARD':
      return <OutlinesCard key={idx} action={action} />;
    case 'CONTENT_CARD':
      return <ContentCard key={idx} action={action} />;
    case 'SOCIAL_ACCOUNT_CTA':
      return <SocialAccountCTA key={idx} onNavigate={onNavigate} />;
    case 'SETTINGS_CARD':
      return <SettingsCard key={idx} action={action} />;
    case 'SCHEDULE_CARD':
      return <ScheduleCard key={idx} action={action} onSend={onSend} />;
    case 'THEME_CARD':
      return <ThemeCard key={idx} action={action} onThemeApplied={onThemeApplied} />;
    case 'RESEARCH_CARD':
      return <ResearchCard key={idx} action={action} />;
    case 'IDENTITY_CARD':
      return <IdentityCard key={idx} action={action} />;
    case 'IMAGE_CARD':
      return <ImageAssetCard key={idx} action={action} />;
    // CONNECT_IG_CARD renders inline connect UI in ActivationPage only.
    default:
      return null;
  }
}

/**
 * Renders all action cards from an array. Used by both chat page variants.
 */
export function AgentCardList({
  actions,
  onNavigate,
  onSend,
  onThemeApplied,
}: {
  actions: AgentAction[];
  onNavigate?: (_page: PageKey) => void;
  onSend?: (_text: string) => void;
  onThemeApplied?: (_colors: ThemeColors) => void;
}) {
  if (!actions || actions.length === 0) return null;
  return (
    <div className="ob-cards">
      {actions.map((action, i) => renderActionCard(action, i, onNavigate, onSend, onThemeApplied))}
    </div>
  );
}
